import { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import type { WorkbookInstance } from '@fortune-sheet/react';
import { getInternals } from './workbookInternals';
import { cellToRefText } from './formulaRefs';
import { goToRange, activeCellRowCol, resolveGoToRef } from './sheetActions';
import { usePortalContainer } from './usePortalContainer';
import { NAME_BOX_CONTAINER_SELECTOR } from './selectors';
import styles from './NameBox.module.scss';

/** Shared with the drawer's `isNameBoxTarget` (the Esc-policy pass-through
 *  and Lane B target gate). */
export const NAME_BOX_INPUT_ID = 'aica-sheet-name-box';

export interface NameBoxProps {
    wrapperRef: React.RefObject<HTMLElement>;
    workbookRef: React.RefObject<WorkbookInstance>;
    /** Workbook remount key — the container is a brand-new DOM node on every
     *  remount, same MutationObserver-re-find pattern as useSheetTabWheelScroll. */
    remountKey: string | number;
    workbookMounted: boolean;
    /** Bumped by the drawer's `afterSelectionChange` hook — the trigger to
     *  re-read the active cell (the hook only tells us SOMETHING changed; the
     *  value itself always comes from getContext()/getSelection()). */
    selectionTick: number;
    onRefocusGrid: () => void;
}

/**
 * An app `<input>` portaled into the library's own
 * `.fortune-name-box-container` (its own `.fortune-name-box` div is hidden
 * via scoped CSS). Because this component is a REACT SIBLING of the Workbook
 * subtree (rendered outside it), its keystrokes never reach Lane A's
 * `onKeyDownCapture` or the library's own container handler, regardless of
 * where the portal target physically sits in the DOM — closing the trap
 * where typing a ref into the old library box silently overwrote the active
 * cell instead of navigating to it.
 */
const NameBox: React.FC<NameBoxProps> = ({ wrapperRef, workbookRef, remountKey, workbookMounted, selectionTick, onRefocusGrid }) => {
    const container = usePortalContainer(wrapperRef, NAME_BOX_CONTAINER_SELECTOR, remountKey, workbookMounted);
    const [value, setValue] = useState('');
    const editingRef = useRef(false);

    const readActiveCellRef = (): string => {
        const wb = workbookRef.current;
        const internals = getInternals(wb);
        const [r, c] = activeCellRowCol(wb, internals);
        return cellToRefText(r, c);
    };

    useEffect(() => {
        if (editingRef.current) return; // don't clobber an in-progress edit
        setValue(readActiveCellRef());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectionTick, workbookMounted]);

    if (!container) return null;

    return createPortal(
        <input
            id={NAME_BOX_INPUT_ID}
            className={styles.Input}
            value={value}
            aria-label="Name box"
            onFocus={() => {
                editingRef.current = true;
            }}
            onBlur={() => {
                editingRef.current = false;
            }}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
                // Never reaches the library's own container handler in real
                // React portal semantics (see the class doc), but stop it
                // here too so a document-level listener (Lane B, the
                // library's own global keydown) doesn't act on these keys.
                e.stopPropagation();
                if (e.key === 'Enter') {
                    e.preventDefault();
                    const wb = workbookRef.current;
                    if (!wb) return;
                    const result = resolveGoToRef(wb, value);
                    if (!result.ok) {
                        // The Name Box has no feedback for an invalid ref
                        // (GoToDialog shows the same message inline) — a
                        // bare <input> has no inline-error slot, so this
                        // surfaces via the same toast pattern the app's
                        // other paste/save error messages already use.
                        toast.error(result.error);
                        setValue(readActiveCellRef());
                        return;
                    }
                    goToRange(wb, result.range, result.sheetId);
                    editingRef.current = false;
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    editingRef.current = false;
                    setValue(readActiveCellRef());
                    onRefocusGrid();
                }
            }}
        />,
        container,
    );
};

export default NameBox;
